/**
 * Account connections API client (QTP OMS PublicView).
 * CU 86bbcd4cg — generic portal connection UX.
 *
 * Never store or display credential_ref or secret material from responses.
 * Secrets only on POST …/credentials body, then discard from UI state.
 */

import { BaseApiClient } from './base-client';
import type { ApiResponse } from './config';

/** Matches QTP models.AuthType for MVP fields. */
export type ConnectionAuthType =
  | 'api_key_secret'
  | 'session_token'
  | 'oauth2'
  | 'hmac_signed'
  | 'mtls_cert'
  | 'fix'
  | string;

/** QTP connection row status (not HTTP ConnectionStatus component). */
export type ConnectionLifecycleStatus =
  | 'pending'
  | 'active'
  | 'degraded'
  | 'revoked'
  | 'error'
  | string;

export type ConnectionEnvironment = 'paper' | 'live' | string;

/** Safe projection — no credential_ref (QTP PublicView). */
export interface PublicConnection {
  connection_id: string;
  tenant_id: string;
  user_id: string;
  venue_id: string;
  broker_account_id?: string;
  display_name?: string;
  auth_type?: ConnectionAuthType;
  scopes?: string[];
  environment: ConnectionEnvironment;
  status: ConnectionLifecycleStatus;
  capabilities?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
  metadata?: Record<string, string>;
}

export interface CreateConnectionBody {
  tenant_id?: string;
  user_id?: string;
  venue_id: string;
  display_name?: string;
  auth_type: ConnectionAuthType;
  environment: ConnectionEnvironment;
  scopes?: string[];
  metadata?: Record<string, string>;
  /** Never send secrets here — QTP rejects raw keys on create. */
}

export interface PutCredentialsBody {
  credentials: Record<string, string>;
}

export interface CredentialsResult {
  connection: PublicConnection;
  has_credentials?: boolean;
}

/** Auth-type → credential field definitions for generic forms. */
export const CONNECTION_CREDENTIAL_FIELDS: Record<
  string,
  ReadonlyArray<{ name: string; label: string; password?: boolean }>
> = {
  api_key_secret: [
    { name: 'api_key', label: 'API Key', password: true },
    { name: 'api_secret', label: 'API Secret', password: true },
  ],
  session_token: [{ name: 'access_token', label: 'Session access token', password: true }],
};

export const MVP_AUTH_TYPES: ConnectionAuthType[] = ['api_key_secret', 'session_token'];

/** Offline fallback only — live UI loads GET /api/v1/venues from DB-backed catalog. */
export const MVP_VENUES: ReadonlyArray<{ id: string; label: string; defaultAuth: ConnectionAuthType }> = [];

function unwrapList(raw: unknown): PublicConnection[] {
  if (Array.isArray(raw)) return raw as PublicConnection[];
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.connections)) return o.connections as PublicConnection[];
    if (Array.isArray(o.data)) return o.data as PublicConnection[];
  }
  return [];
}

function unwrapConnection(raw: unknown): PublicConnection {
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    if (o.connection && typeof o.connection === 'object') {
      return o.connection as PublicConnection;
    }
    if (o.data && typeof o.data === 'object' && !Array.isArray(o.data)) {
      const d = o.data as Record<string, unknown>;
      if (d.connection) return d.connection as PublicConnection;
      return d as unknown as PublicConnection;
    }
  }
  return raw as PublicConnection;
}

/** Discover catalog entry from GET /api/v1/venues (CU 86bbcdxfe). */
export interface VenueCatalogEntry {
  venue_id: string;
  display_name: string;
  auth_types: string[];
  environments: string[];
  asset_classes?: string[];
  description?: string;
  default_auth_type?: string;
}

export type WorkspaceSectionStatus = 'ready' | 'unsupported' | 'error';

export interface ConnectionWorkspaceSnapshot {
  connection: PublicConnection;
  provenance: {
    connection_id: string;
    venue_id: string;
    broker_account_id?: string;
    environment: ConnectionEnvironment;
    generated_at: string;
  };
  health?: {
    status: string;
    connected: boolean;
    message?: string;
    checked_at: string;
  };
  account?: {
    venue_id?: string;
    broker_account_id?: string;
    account_type?: string;
    currency?: string;
    environment?: ConnectionEnvironment;
    display_name?: string;
    status?: string;
    updated_at?: string;
  };
  balances?: {
    currency?: string;
    total_balance: number;
    available_balance: number;
    equity: number;
    buying_power: number;
    used_margin?: number;
    updated_at: string;
  };
  positions?: Array<Record<string, unknown>>;
  orders?: Array<Record<string, unknown>>;
  sections: Record<string, { status: WorkspaceSectionStatus }>;
  status: 'complete' | 'partial';
  as_of: string;
}

export type EquityHistoryRange = '1D' | '1W' | '1M';
export type EquityHistoryStatus = 'ready' | 'empty';

export interface EquityHistoryPoint {
  observed_at: string;
  equity: number;
  currency: string;
}

export interface ConnectionEquityHistory {
  connection_id: string;
  points: EquityHistoryPoint[];
  range: EquityHistoryRange;
  currency: string;
  generated_at: string;
  status: EquityHistoryStatus;
}

function isEquityHistoryRange(value: unknown): value is EquityHistoryRange {
  return value === '1D' || value === '1W' || value === '1M';
}

/** Parse only finite, timestamped observations; never manufacture chart points. */
export function parseConnectionEquityHistory(
  raw: unknown,
  requestedRange: EquityHistoryRange
): ConnectionEquityHistory {
  const wrapped =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  const record =
    wrapped.data && typeof wrapped.data === 'object' && !Array.isArray(wrapped.data)
      ? (wrapped.data as Record<string, unknown>)
      : wrapped;
  const range = isEquityHistoryRange(record.range) ? record.range : requestedRange;
  const responseCurrency =
    typeof record.currency === 'string' ? record.currency.trim().toUpperCase() : '';
  const points = (Array.isArray(record.points) ? record.points : [])
    .flatMap((value): EquityHistoryPoint[] => {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
      const point = value as Record<string, unknown>;
      if (typeof point.equity !== 'number' || !Number.isFinite(point.equity)) return [];
      if (typeof point.observed_at !== 'string') return [];
      const observedAt = new Date(point.observed_at);
      if (Number.isNaN(observedAt.getTime())) return [];
      const currency =
        typeof point.currency === 'string' && point.currency.trim()
          ? point.currency.trim().toUpperCase()
          : responseCurrency;
      return [{
        observed_at: observedAt.toISOString(),
        equity: point.equity,
        currency,
      }];
    })
    .sort((a, b) => Date.parse(a.observed_at) - Date.parse(b.observed_at));
  const explicitlyEmpty = record.status === 'empty';
  const status: EquityHistoryStatus =
    !explicitlyEmpty && points.length > 0 ? 'ready' : 'empty';
  const parsedPoints = status === 'ready' ? points : [];
  const generatedAt =
    typeof record.generated_at === 'string' &&
    Number.isFinite(Date.parse(record.generated_at))
      ? new Date(record.generated_at).toISOString()
      : '';

  return {
    connection_id:
      typeof record.connection_id === 'string' ? record.connection_id.trim() : '',
    points: parsedPoints,
    range,
    currency:
      responseCurrency ||
      parsedPoints[parsedPoints.length - 1]?.currency ||
      '',
    generated_at: generatedAt,
    status,
  };
}

export class ConnectionsApiClient extends BaseApiClient {
  /** Venue discover catalog (no secrets). */
  async listVenues(): Promise<ApiResponse<VenueCatalogEntry[]>> {
    const res = await this.get('/api/v1/venues');
    const body = res?.data ?? res;
    let venues: VenueCatalogEntry[] = [];
    if (body && typeof body === 'object') {
      const o = body as Record<string, unknown>;
      if (Array.isArray(o.venues)) venues = o.venues as VenueCatalogEntry[];
      else if (Array.isArray(body)) venues = body as VenueCatalogEntry[];
    }
    return {
      data: venues,
      status: res?.status ?? 200,
      success: res?.success ?? true,
      message: res?.message,
    };
  }

  async list(params?: { tenant_id?: string; user_id?: string }): Promise<ApiResponse<PublicConnection[]>> {
    const q = new URLSearchParams();
    if (params?.tenant_id) q.set('tenant_id', params.tenant_id);
    if (params?.user_id) q.set('user_id', params.user_id);
    const qs = q.toString();
    const path = qs ? `/api/v1/connections?${qs}` : '/api/v1/connections';
    const res = await this.get(path);
    const body = res?.data ?? res;
    return {
      data: unwrapList(body),
      status: res?.status ?? 200,
      success: res?.success ?? true,
      message: res?.message,
    };
  }

  async getById(connectionId: string): Promise<ApiResponse<PublicConnection>> {
    const res = await this.get(`/api/v1/connections/${encodeURIComponent(connectionId)}`);
    const body = res?.data ?? res;
    return {
      data: unwrapConnection(body),
      status: res?.status ?? 200,
      success: res?.success ?? true,
      message: res?.message,
    };
  }

  async getWorkspace(connectionId: string): Promise<ApiResponse<ConnectionWorkspaceSnapshot>> {
    const res = await this.get(
      `/api/v1/connections/${encodeURIComponent(connectionId)}/workspace`
    );
    return {
      data: (res?.data ?? res) as ConnectionWorkspaceSnapshot,
      status: res?.status ?? 200,
      success: res?.success ?? true,
      message: res?.message,
    };
  }

  async getEquityHistory(
    connectionId: string,
    range: EquityHistoryRange
  ): Promise<ApiResponse<ConnectionEquityHistory>> {
    const query = new URLSearchParams({ range });
    const res = await this.get(
      `/api/v1/connections/${encodeURIComponent(connectionId)}/equity-history?${query}`
    );
    return {
      data: parseConnectionEquityHistory(res?.data ?? res, range),
      status: res?.status ?? 200,
      success: res?.success ?? true,
      message: res?.message,
    };
  }

  async create(body: CreateConnectionBody): Promise<ApiResponse<PublicConnection>> {
    const res = await this.post('/api/v1/connections', body);
    const raw = res?.data ?? res;
    return {
      data: unwrapConnection(raw),
      status: res?.status ?? 201,
      success: res?.success ?? true,
      message: res?.message,
    };
  }

  async putCredentials(
    connectionId: string,
    credentials: Record<string, string>
  ): Promise<ApiResponse<CredentialsResult>> {
    const res = await this.post(`/api/v1/connections/${encodeURIComponent(connectionId)}/credentials`, {
      credentials,
    } satisfies PutCredentialsBody);
    const raw = res?.data ?? res;
    if (raw && typeof raw === 'object') {
      const o = raw as Record<string, unknown>;
      return {
        data: {
          connection: unwrapConnection(o.connection ?? o),
          has_credentials: Boolean(o.has_credentials),
        },
        status: res?.status ?? 200,
        success: res?.success ?? true,
        message: res?.message,
      };
    }
    return {
      data: { connection: unwrapConnection(raw), has_credentials: true },
      status: res?.status ?? 200,
      success: res?.success ?? true,
      message: res?.message,
    };
  }

  async validate(connectionId: string): Promise<ApiResponse<PublicConnection>> {
    const res = await this.post(`/api/v1/connections/${encodeURIComponent(connectionId)}/validate`, {});
    const raw = res?.data ?? res;
    return {
      data: unwrapConnection(raw),
      status: res?.status ?? 200,
      success: res?.success ?? true,
      message: res?.message,
    };
  }

  /** Re-bind venue session (same as validate for api_key_secret / session_token). */
  async refresh(connectionId: string): Promise<ApiResponse<PublicConnection>> {
    const res = await this.post(`/api/v1/connections/${encodeURIComponent(connectionId)}/refresh`, {});
    const raw = res?.data ?? res;
    return {
      data: unwrapConnection(raw),
      status: res?.status ?? 200,
      success: res?.success ?? true,
      message: res?.message,
    };
  }

  async revoke(connectionId: string): Promise<ApiResponse<PublicConnection>> {
    const res = await this.post(`/api/v1/connections/${encodeURIComponent(connectionId)}/revoke`, {});
    const raw = res?.data ?? res;
    return {
      data: unwrapConnection(raw),
      status: res?.status ?? 200,
      success: res?.success ?? true,
      message: res?.message,
    };
  }
}
